// Status.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { FaSyncAlt } from 'react-icons/fa';
import MapWithAlerts from 'src/Components/Visualize2D/MapWithAlerts';
import type { CabinetBox } from 'src/utils/visualization';
import { queryKeys } from 'src/queries/queryKeys';
import './Status.css';

import { useAuth } from 'src/Contexts/AuthContext';
import { useSelectedInstallation } from 'src/Contexts/SelectedInstallationContext';
import { useToast } from 'src/Contexts/ToastContext';
import {
  useAlertsQuery,
  useAnalyticsDataQuery,
  useInstallationQuery,
  useLatestSnapshotQuery,
  usePolygonsQuery,
} from 'src/queries';

import CabinetStatusBar from './CabinetStatusBar';
import ColorScaleControl from './ColorScaleControl';
import FeatureVisibilityControl from './FeatureVisibilityControl';
import ManualAlertOverlay from './ManualAlertOverlay';
import TemporalPlot from './TemporalPlot';
import VoltageComboSelector from './VoltageComboSelector';

const Status = () => {
  const { selectedInstallationID } = useSelectedInstallation();
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Manual refresh: invalidate all measurement + alert queries so the map,
  // cabinet bar, timeline and alert banner refetch immediately instead of
  // waiting for the 5-minute poll (or a stale long-lived tab's cache).
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.measurements.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all }),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [queryClient]);
  const { currentUser } = useAuth();
  const isAdmin = Boolean(currentUser?.is_admin);

  // Map/timeline view: raw voltage vs per-contact deviation (value_z).
  // Deviation cancels weather/structure so leaks stand out; see
  // plans/PIPELINE_PER_CONTACT_DEVIATION.md.
  const [viewMode, setViewMode] = useState<'voltage' | 'deviation'>('voltage');

  // Live color-scale preview from the admin ColorScaleControl (not yet saved).
  // Cleared on save/reset and on view-mode switch.
  const [colorPreview, setColorPreview] = useState<{ min: number; max: number } | null>(null);

  // Admin-only manual alert picker. When active, clicks on the viz set the
  // draft focal point. Submitting the form sends POST /api/v2/alerts.
  const [manualAlertActive, setManualAlertActive] = useState(false);
  const [draftFocusPoint, setDraftFocusPoint] = useState<{ x: number; y: number } | null>(null);
  // Reset picker state on installation switch — the draft is in the previous
  // installation's coordinate space. Per React's "Adjusting state on prop
  // change" pattern: a render-time check beats an effect.
  const [pickerInstallationId, setPickerInstallationId] = useState(selectedInstallationID);
  if (pickerInstallationId !== selectedInstallationID) {
    setPickerInstallationId(selectedInstallationID);
    setManualAlertActive(false);
    setDraftFocusPoint(null);
  }

  // Selection is keyed by installation ID so an installation switch
  // automatically invalidates the prior timestamp/combo without an effect.
  const [selection, setSelection] = useState<{
    id: string;
    timestamp: string | null;
    combo: string | null;
  } | null>(null);
  const activeSelection = selection?.id === selectedInstallationID ? selection : null;
  const selectedTimestamp = activeSelection?.timestamp ?? null;
  const selectedCombo = activeSelection?.combo ?? null;

  // Stable identities: TemporalPlot's draw effect depends on onTimestampSelect,
  // so a fresh function each render would re-run it (selectAll('*').remove()),
  // wiping the selected-timestamp marker. Use the setSelection updater form so
  // these don't depend on activeSelection and can stay referentially stable.
  const setSelectedTimestamp = useCallback(
    (timestamp: string | null) => {
      setSelection((prev) => ({
        id: selectedInstallationID ?? '',
        timestamp,
        combo: prev?.id === selectedInstallationID ? (prev?.combo ?? null) : null,
      }));
    },
    [selectedInstallationID]
  );
  const setSelectedCombo = useCallback(
    (combo: string | null) => {
      setSelection({ id: selectedInstallationID ?? '', timestamp: null, combo });
    },
    [selectedInstallationID]
  );

  // Fetch ONE snapshot for the map: the latest, or the frame at the timestamp
  // the user clicked on the temporal plot. Previously this pulled the last 50
  // full snapshots (each with a large interpolated grid) just to display one.
  const {
    data: activeSnapshot,
    isLoading: snapshotLoading,
    isFetching: snapshotFetching,
    isError: snapshotError,
  } = useLatestSnapshotQuery(selectedInstallationID, selectedTimestamp);

  // Always-live latest snapshot for the cabinet status bar: operational
  // status must not follow a pinned historic frame. When no timestamp is
  // pinned this shares the exact query (same key) with activeSnapshot.
  const { data: liveSnapshot } = useLatestSnapshotQuery(selectedInstallationID, null);
  const liveCabinetStatus =
    liveSnapshot && liveSnapshot.installation_id === selectedInstallationID
      ? liveSnapshot.cabinet_status
      : null;

  const {
    data: polygons = [],
    isLoading: polygonsLoading,
    isError: polygonsError,
  } = usePolygonsQuery(selectedInstallationID);

  const { data: analyticsData, isError: analyticsError } = useAnalyticsDataQuery(
    selectedInstallationID,
    selectedCombo
  );

  // Active alerts on this installation. Leakage alerts render as persistent
  // red markers on the live viz (via focus_point); dead(man) alerts — incl.
  // per-cabinet ones ("Schaltschrank 5: ...") — have no spatial focal point
  // and only appear in the warning banner above the map.
  const { data: activeAlerts = [] } = useAlertsQuery(
    {
      installation_id: selectedInstallationID,
      type: ['leakage', 'power', 'offline', 'stale'],
      status: ['active', 'acknowledged'],
    },
    true
  );
  const activeAlertMarkers = useMemo(
    () =>
      activeAlerts
        .filter((a) => a.focus_point != null)
        .map((a) => ({
          x: a.focus_point!.x,
          y: a.focus_point!.y,
          color: a.severity === 'critical' ? '#d32f2f' : '#f57c00',
          title: a.message || t(`alarm.type.${a.type}`),
        })),
    [activeAlerts, t]
  );

  // During an installation switch the snapshot query serves the PREVIOUS
  // installation's data (keepPreviousData) until the new fetch resolves. Drawing
  // that against the new installation's polygons produces a garbled intermediate
  // frame. "Not ready" means specifically: we're holding a non-null snapshot
  // whose installation_id doesn't match the selected one (a stale placeholder).
  // A settled null/empty result IS ready — it means "no data for this install".
  const snapshotStale =
    !!activeSnapshot &&
    !!activeSnapshot.installation_id &&
    activeSnapshot.installation_id !== selectedInstallationID;
  const snapshotReady = !snapshotStale;

  const rawPoints = useMemo(
    () => (snapshotReady ? activeSnapshot?.raw_measurements || [] : []),
    [snapshotReady, activeSnapshot]
  );
  const measurementTimestamp = snapshotReady ? activeSnapshot?.measurement_timestamp || null : null;

  // Deviation features only exist on enriched snapshots. If the active snapshot
  // has none (older data / interpolation skipped), fall back to voltage so the
  // map never goes blank.
  // Installation detail — for admin controls and per-installation feature flags.
  const { data: installation } = useInstallationQuery(selectedInstallationID ?? '');

  // Per-installation feature visibility for NON-admin users (admins always see
  // both, so they can manage/evaluate). timeline default-on; deviation
  // (experimental) default-off.
  const cfg = installation?.configuration ?? {};
  const timelineVisible = isAdmin || cfg.timeline_visible !== false;
  const deviationVisible = isAdmin || cfg.deviation_visible === true;
  // MPLE and interpolation are HARD toggles (no admin exception); point radius
  // applies to all.
  const showMple = cfg.mple_visible !== false;
  const showInterpolation = cfg.interpolation_visible !== false;
  const pointRadius = typeof cfg.point_radius === 'number' ? cfg.point_radius : 3.5;

  const devGrid = snapshotReady ? activeSnapshot?.interpolated_data_dev || null : null;
  const hasDeviation =
    deviationVisible && (!!devGrid || rawPoints.some((p: any) => p.value_z != null));
  const effectiveMode = viewMode === 'deviation' && hasDeviation ? 'deviation' : 'voltage';

  // In deviation mode the viz colors by value_z (CoordinateSystem reads _value),
  // so remap _value -> value_z and swap in the deviation grid.
  const points = useMemo(() => {
    if (effectiveMode !== 'deviation') return rawPoints;
    return rawPoints.map((p: any) => ({ ...p, _value: p.value_z ?? 0 }));
  }, [rawPoints, effectiveMode]);
  const interpolatedData = !snapshotReady
    ? null
    : effectiveMode === 'deviation'
      ? devGrid
      : activeSnapshot?.interpolated_data || null;

  // Cabinet alert boxes: for every active alert that references a Schaltschrank,
  // box that cabinet's area so the operator sees *where* the problem is. Covers
  // per-cabinet deadman alerts, which have no focus_point and would otherwise
  // only appear in the banner. One box per cabinet (dedupe across alerts,
  // keeping the most severe color).
  //
  // Geometry source, in priority order:
  //   1. cabinet_bounds from the LIVE snapshot — persistent per-cabinet bounds
  //      the backend computes from coordinate_map. Works even when the cabinet
  //      is offline (a deadman cabinet has no live points), which is exactly the
  //      case this feature is for.
  //   2. Fallback: bounding box of the cabinet's live points in the current
  //      frame — for installations whose coordinate_map isn't seeded yet.
  const cabinetAlertBoxes = useMemo<CabinetBox[]>(() => {
    const cabinetsWithAlerts = new Map<string, { critical: boolean; titles: string[] }>();
    for (const a of activeAlerts) {
      const nr = a.schaltschranknummer;
      if (nr == null || nr === '') continue;
      const key = String(nr);
      const entry = cabinetsWithAlerts.get(key) ?? { critical: false, titles: [] };
      if (a.severity === 'critical') entry.critical = true;
      entry.titles.push(a.message || t(`alarm.type.${a.type}`));
      cabinetsWithAlerts.set(key, entry);
    }
    if (cabinetsWithAlerts.size === 0) return [];

    const cabinetBounds =
      liveSnapshot && liveSnapshot.installation_id === selectedInstallationID
        ? liveSnapshot.cabinet_bounds
        : null;

    // Resolve bounds per alerted cabinet: prefer persistent coordinate_map
    // bounds, else derive from this frame's points.
    const bounds = new Map<string, { x0: number; y0: number; x1: number; y1: number }>();
    for (const key of cabinetsWithAlerts.keys()) {
      const cb = cabinetBounds?.[key];
      if (cb) bounds.set(key, { x0: cb.x0, y0: cb.y0, x1: cb.x1, y1: cb.y1 });
    }
    // Point-derived fallback bounds, kept separate so persistent bounds always win.
    const pointBounds = new Map<string, { x0: number; y0: number; x1: number; y1: number }>();
    for (const p of points) {
      const nr = p.schaltschranknummer;
      if (nr == null || nr === '') continue;
      const key = String(nr);
      // Persistent bounds win; only fall back to points for cabinets without them.
      if (!cabinetsWithAlerts.has(key) || bounds.has(key)) continue;
      // Mark point-derived bounds so we don't mix them with a coordinate_map box.
      const b = pointBounds.get(key);
      if (!b) {
        pointBounds.set(key, { x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      } else {
        b.x0 = Math.min(b.x0, p.x);
        b.y0 = Math.min(b.y0, p.y);
        b.x1 = Math.max(b.x1, p.x);
        b.y1 = Math.max(b.y1, p.y);
      }
    }
    for (const [key, b] of pointBounds) bounds.set(key, b);

    const boxes: CabinetBox[] = [];
    for (const [key, b] of bounds) {
      // Uniform world-unit padding on all four sides so the gap looks even in
      // both axes (a per-axis % pad made wide cabinets hug tightly in y but
      // loosely in x). Base it on the larger span so it scales with the cabinet.
      const span = Math.max(b.x1 - b.x0, b.y1 - b.y0);
      const pad = Math.max(span * 0.04, 0.2);
      const meta = cabinetsWithAlerts.get(key)!;
      boxes.push({
        schaltschranknummer: key,
        x0: b.x0 - pad,
        y0: b.y0 - pad,
        x1: b.x1 + pad,
        y1: b.y1 + pad,
        color: meta.critical ? '#d32f2f' : '#f57c00',
        title: meta.titles.join('\n'),
      });
    }
    return boxes;
  }, [activeAlerts, points, liveSnapshot, selectedInstallationID, t]);

  // Switching the view mode invalidates the in-flight color preview (it was for
  // the other scale). Render-time reset per React's adjust-state-on-prop pattern.
  const [colorPreviewMode, setColorPreviewMode] = useState(effectiveMode);
  if (colorPreviewMode !== effectiveMode) {
    setColorPreviewMode(effectiveMode);
    setColorPreview(null);
  }

  // Domain to prefill the control with: the saved config for this mode if set,
  // else a sensible auto guess from the current points (mirrors CoordinateSystem).
  const effectiveDomain = useMemo<[number, number] | null>(() => {
    const c = installation?.configuration ?? {};
    const sMin = effectiveMode === 'deviation' ? c.color_scale_dev_min : c.color_scale_min;
    const sMax = effectiveMode === 'deviation' ? c.color_scale_dev_max : c.color_scale_max;
    if (typeof sMin === 'number' && typeof sMax === 'number' && sMin < sMax) return [sMin, sMax];
    const vals = points
      .map((p: any) => p._value)
      .filter((v: unknown): v is number => typeof v === 'number' && isFinite(v));
    if (vals.length === 0) return null;
    if (effectiveMode === 'deviation') {
      const absSorted = vals.map((v: number) => Math.abs(v)).sort((a: number, b: number) => a - b);
      const p95 = absSorted[Math.floor(absSorted.length * 0.95)] || 1;
      return [-p95, p95];
    }
    const sorted = [...vals].sort((a: number, b: number) => a - b);
    return [sorted[Math.floor(sorted.length * 0.05)], sorted[Math.floor(sorted.length * 0.95)]];
  }, [installation, effectiveMode, points]);

  useEffect(() => {
    if (snapshotError) {
      toast.error('Failed to load measurement data');
    }
    if (polygonsError) {
      toast.error('Failed to load installation structure');
    }
    if (analyticsError) {
      toast.error('Failed to load timeseries data');
    }
  }, [snapshotError, polygonsError, analyticsError, toast]);

  // Gate first paint only on what the MAP needs: the snapshot + polygons.
  // Analytics (timeseries + voltage combos) feeds the temporal plot and combo
  // selector, which already render conditionally below once their data arrives
  // — so don't block the whole page on the (slower) analytics query.
  // `!snapshotReady` covers the installation switch: keepPreviousData keeps
  // isLoading false, but the snapshot is still the previous installation's, so
  // show the spinner rather than flash stale data or the no-data message.
  if (snapshotLoading || polygonsLoading || !snapshotReady) {
    return (
      <div className="status-page">
        <div className="status-loading">
          <div className="status-spinner" />
          <p>{t('Loading...')}</p>
        </div>
      </div>
    );
  }

  // Snapshot is for this installation and genuinely has no points.
  if (points.length === 0) {
    return (
      <div className="status-page">
        <div className="status-no-data">
          <p>{t('No measurement data available yet for this installation.')}</p>
        </div>
      </div>
    );
  }

  const availableCombos = analyticsData?.available_combos ?? [];
  const hasMultipleVoltages = availableCombos.length > 1;
  const effectiveCombo =
    selectedCombo && availableCombos.includes(selectedCombo)
      ? selectedCombo
      : (availableCombos[0] ?? null);
  const timeseries = analyticsData?.timeseries ?? {};

  return (
    <div className="status-page">
      <div className="status-controls">
        <button
          type="button"
          className="status-refresh-btn"
          onClick={() => {
            void handleRefresh();
          }}
          disabled={refreshing}
          title={t('status.refresh')}
          aria-label={t('status.refresh')}
        >
          <FaSyncAlt className={refreshing ? 'status-refresh-icon--spinning' : ''} />
        </button>
        {hasMultipleVoltages && (
          <VoltageComboSelector
            availableCombos={availableCombos}
            selectedCombo={effectiveCombo}
            onComboChange={(combo) => {
              setSelectedCombo(combo);
              setSelectedTimestamp(null);
            }}
          />
        )}
        {hasDeviation && (
          <div className="status-viewtoggle" role="group" aria-label={t('status.viewMode')}>
            <button
              type="button"
              className={`status-viewtoggle-btn ${effectiveMode === 'voltage' ? 'is-active' : ''}`}
              onClick={() => setViewMode('voltage')}
            >
              {t('status.view.voltage')}
            </button>
            <button
              type="button"
              className={`status-viewtoggle-btn ${effectiveMode === 'deviation' ? 'is-active' : ''}`}
              onClick={() => setViewMode('deviation')}
            >
              {t('status.view.deviation')}
            </button>
          </div>
        )}
        {isAdmin && installation && (
          <ColorScaleControl
            // Remount on installation/mode change so inputs re-prefill cleanly.
            key={`${installation.id}-${effectiveMode}`}
            installation={installation}
            mode={effectiveMode}
            effectiveDomain={effectiveDomain}
            onPreview={setColorPreview}
          />
        )}
        {isAdmin && installation && (
          <FeatureVisibilityControl key={installation.id} installation={installation} />
        )}
      </div>
      {isAdmin && selectedInstallationID && (
        <ManualAlertOverlay
          // Keying by installation forces a remount on switch, resetting the
          // picker + draft state so we never apply a focal point from a prior
          // installation's coordinate space.
          key={selectedInstallationID}
          installationId={selectedInstallationID}
          draftFocusPoint={draftFocusPoint}
          onDraftClear={() => setDraftFocusPoint(null)}
          pickerActive={manualAlertActive}
          onPickerToggle={(active) => {
            setManualAlertActive(active);
            if (!active) setDraftFocusPoint(null);
          }}
          snapshotPoints={points}
          snapshotTimestamp={measurementTimestamp}
        />
      )}
      <CabinetStatusBar
        cabinetStatus={liveCabinetStatus}
        // Hours everywhere; default matches the backend evaluator's 24h.
        deadmanThresholdHours={cfg.deadman_threshold || installation?.deadman_threshold || 24}
      />
      <div className="status-map">
        <MapWithAlerts
          points={points}
          alerts={activeAlerts}
          polygons={polygons}
          interpolatedData={interpolatedData}
          measurementTimestamp={measurementTimestamp}
          // Map-local loading: true while a newly-selected timeline frame is
          // being fetched. The previous frame stays visible underneath
          // (keepPreviousData) and the timeline/rest of the page is unaffected.
          loading={snapshotFetching}
          onBackgroundClick={manualAlertActive ? (x, y) => setDraftFocusPoint({ x, y }) : undefined}
          focusPoint={manualAlertActive ? draftFocusPoint : null}
          focusPoints={activeAlertMarkers}
          cabinetBoxes={cabinetAlertBoxes}
          showMple={showMple}
          showInterpolation={showInterpolation}
          pointRadius={pointRadius}
          mode={effectiveMode}
          colorOverride={colorPreview}
        />
      </div>
      {timelineVisible && Object.keys(timeseries).length > 0 && (
        <div className="status-timeseries">
          <TemporalPlot
            timeseries={timeseries}
            selectedTimestamp={selectedTimestamp}
            onTimestampSelect={setSelectedTimestamp}
            mode={effectiveMode}
          />
        </div>
      )}
    </div>
  );
};

export default Status;
